import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";
import { queryKeys } from "@/lib/queryClient";
import { getErrorMessage } from "@/lib/errorHandler";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Users, ChevronDown, ChevronUp, UserPlus, Upload, Download, FileText, Calendar, TrendingUp, Search, ChevronLeft, ChevronRight, Layers, MapPin, Pencil } from "lucide-react";
import { z } from "zod";

const groupSchema = z.object({
  name: z.string().min(2, "Group name must be at least 2 characters").max(100, "Group name must be under 100 characters"),
  description: z.string().max(500, "Description must be under 500 characters").optional().or(z.literal("")),
});

export default function Groups() {
  const { user } = useAuth();
  const { selectedOrg, isSuperadmin } = useOrganizationContext();
  const { campuses, selectedCampus, isOrgAdmin, isMultiCampusAdmin } = useCampusContext();
  const canManageGroups = isSuperadmin || user?.permissions?.can_manage_groups === true;
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const [memberDialog, setMemberDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [expandedSubgroup, setExpandedSubgroup] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", campus_id: "" });
  const [parentGroupForCreate, setParentGroupForCreate] = useState(null); // { id, name } when creating subgroup
  const [memberForm, setMemberForm] = useState({ name: "", email: "" });
  const [bulkCsv, setBulkCsv] = useState("");
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [editDialog, setEditDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", campus_id: "" });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [superadminCampuses, setSuperadminCampuses] = useState([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [campusFilter, setCampusFilter] = useState("");

  const orgId = selectedOrg?.id;

  // Determine effective campus filter
  const effectiveCampusId = (isOrgAdmin || isMultiCampusAdmin || isSuperadmin) ? (campusFilter || undefined) : undefined;

  // For superadmin: fetch campuses for the selected org (CampusContext doesn't load for superadmin)
  useEffect(() => {
    if (isSuperadmin && orgId) {
      API.get('/campuses', { params: { organization_id: orgId } })
        .then(res => setSuperadminCampuses(res.data || []))
        .catch(() => setSuperadminCampuses([]));
    } else {
      setSuperadminCampuses([]);
    }
  }, [isSuperadmin, orgId]);

  // Campus list for dialogs and display: org admin uses CampusContext, superadmin uses local fetch
  const allCampuses = isSuperadmin ? superadminCampuses : campuses;

  // Fetch top-level groups list
  const { data: groupsData, isLoading: loading } = useQuery({
    queryKey: queryKeys.groups.list(page, searchTerm, orgId, effectiveCampusId),
    queryFn: async () => {
      const params = { page, limit: 50, top_level_only: true };
      if (searchTerm) params.search = searchTerm;
      if (isSuperadmin && orgId) {
        params.organization_id = orgId;
      }
      if (effectiveCampusId) {
        params.campus_id = effectiveCampusId;
      }
      const res = await API.get("/groups", { params });
      return res.data;
    },
    enabled: !isSuperadmin || !!selectedOrg,
    placeholderData: keepPreviousData,
  });

  const groups = groupsData?.groups || [];
  const pagination = groupsData?.pagination || { total: 0, pages: 0, limit: 50 };

  // Fetch parent group detail (on-demand)
  const { data: groupDetail } = useQuery({
    queryKey: queryKeys.groups.detail(expandedGroup),
    queryFn: async () => {
      const res = await API.get(`/groups/${expandedGroup}`);
      return res.data;
    },
    enabled: !!expandedGroup,
  });

  // Fetch subgroup detail (on-demand)
  const { data: subgroupDetail } = useQuery({
    queryKey: queryKeys.groups.detail(expandedSubgroup),
    queryFn: async () => {
      const res = await API.get(`/groups/${expandedSubgroup}`);
      return res.data;
    },
    enabled: !!expandedSubgroup,
  });

  const toggleGroupDetail = (groupId) => {
    setExpandedGroup(prev => prev === groupId ? null : groupId);
    setExpandedSubgroup(null); // reset subgroup when switching parent
  };

  const toggleSubgroupDetail = (e, subgroupId) => {
    e.stopPropagation();
    setExpandedSubgroup(prev => prev === subgroupId ? null : subgroupId);
  };

  // Create group mutation
  const createGroupMutation = useMutation({
    mutationFn: (payload) => API.post("/groups", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success(parentGroupForCreate ? "Subgroup created" : "Group created");
      setDialog(false);
      setForm({ name: "", description: "", campus_id: "" });
      setParentGroupForCreate(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, "Failed to create group")),
  });

  const openCreateDialog = (parentGroup = null) => {
    setForm({ name: "", description: "", campus_id: "" });
    setFormErrors({});
    setParentGroupForCreate(parentGroup);
    setDialog(true);
  };

  const createGroup = () => {
    const result = groupSchema.safeParse(form);
    if (!result.success) {
      const errors = {};
      result.error.issues.forEach(issue => { errors[issue.path[0]] = issue.message; });
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    const payload = { name: form.name, description: form.description };
    if (parentGroupForCreate) {
      payload.parent_id = parentGroupForCreate.id;
    } else if (isSuperadmin && orgId) {
      payload.organization_id = orgId;
    }
    if (form.campus_id) payload.campus_id = form.campus_id;
    createGroupMutation.mutate(payload);
  };

  // Delete group mutation
  const deleteGroupMutation = useMutation({
    mutationFn: (id) => API.delete(`/groups/${id}`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success(res.data?.message || "Group deleted");
    },
    onError: () => toast.error("Failed to delete group"),
  });

  const deleteGroup = (group) => {
    const subgroupCount = group.subgroup_count || 0;
    const message = subgroupCount > 0
      ? `Delete "${group.name}" and its ${subgroupCount} subgroup(s)? This cannot be undone.`
      : `Delete "${group.name}"? This cannot be undone.`;
    if (!window.confirm(message)) return;
    deleteGroupMutation.mutate(group.id);
  };

  const deleteSubgroup = (subgroup) => {
    if (!window.confirm(`Delete subgroup "${subgroup.name}"? This cannot be undone.`)) return;
    deleteGroupMutation.mutate(subgroup.id);
    // Refresh parent detail after deletion
    if (expandedGroup) {
      queryClient.invalidateQueries({ queryKey: queryKeys.groups.detail(expandedGroup) });
    }
  };

  // Update group mutation
  const updateGroupMutation = useMutation({
    mutationFn: ({ id, data }) => API.put(`/groups/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success("Group updated");
      setEditDialog(false);
      setEditingGroup(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, "Failed to update group")),
  });

  const openEditDialog = (e, group) => {
    e.stopPropagation();
    setEditingGroup(group);
    setEditForm({ name: group.name, description: group.description || "", campus_id: group.campus_id || "" });
    setEditFormErrors({});
    setEditDialog(true);
  };

  const saveEditGroup = () => {
    const result = groupSchema.safeParse(editForm);
    if (!result.success) {
      const errors = {};
      result.error.issues.forEach(issue => { errors[issue.path[0]] = issue.message; });
      setEditFormErrors(errors);
      return;
    }
    setEditFormErrors({});
    updateGroupMutation.mutate({
      id: editingGroup.id,
      data: { name: editForm.name, description: editForm.description, campus_id: editForm.campus_id || null },
    });
  };

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: ({ groupId, data }) => API.post(`/groups/${groupId}/members`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success("Member added");
      setMemberDialog(false);
      setMemberForm({ name: "", email: "" });
    },
    onError: (err) => toast.error(getErrorMessage(err, "Failed to add member")),
  });

  const addMember = () => {
    if (!memberForm.name.trim() || !memberForm.email.trim()) {
      toast.error("Name and email required");
      return;
    }
    addMemberMutation.mutate({ groupId: activeGroupId, data: memberForm });
  };

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: ({ groupId, memberId }) => API.delete(`/groups/${groupId}/members/${memberId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success("Member removed");
    },
    onError: () => toast.error("Failed to remove member"),
  });

  const removeMember = (groupId, memberId) => {
    removeMemberMutation.mutate({ groupId, memberId });
  };

  // Bulk add members mutation
  const bulkAddMembersMutation = useMutation({
    mutationFn: ({ groupId, members }) => API.post(`/groups/${groupId}/members/bulk`, { members }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success(res.data.message);
      setBulkDialog(false);
      setBulkCsv("");
    },
    onError: (err) => toast.error(getErrorMessage(err, "Failed to add members")),
  });

  const downloadSampleCsv = () => {
    const sample = "email,name\njohn@example.com,John Doe\njane@example.com,Jane Smith";
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "members_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkAddMembers = () => {
    if (!bulkCsv.trim()) {
      toast.error("Please enter member data");
      return;
    }
    const lines = bulkCsv.trim().split("\n");
    const members = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      // Skip header row if present
      if (i === 0 && line.toLowerCase().includes("email") && line.toLowerCase().includes("name")) continue;
      const parts = line.split(",");
      if (parts.length >= 2) {
        const email = parts[0].trim();
        const name = parts.slice(1).join(",").trim();
        if (email && name) {
          members.push({ email, name });
        }
      }
    }
    if (members.length === 0) {
      toast.error("No valid members found. Use format: email,name");
      return;
    }
    bulkAddMembersMutation.mutate({ groupId: activeGroupId, members });
  };

  const renderMembersSection = (detail, groupId, isSub = false) => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="refined-subheader">Members ({detail.member_count || 0})</p>
        {canManageGroups && <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setActiveGroupId(groupId);
              setBulkDialog(true);
            }}
            className="refined-action-button border-slate-300 text-slate-700"
            data-testid={`bulk-add-members-${groupId}`}
          >
            <Upload className="w-3.5 h-3.5 mr-1" /> Bulk Add
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setActiveGroupId(groupId);
              setMemberDialog(true);
            }}
            className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid={`add-member-${groupId}`}
          >
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Member
          </Button>
        </div>}
      </div>
      {detail.members?.length > 0 ? (
        <div className="refined-table">
          <Table>
            <TableHeader className="refined-table-header">
              <TableRow>
                <TableHead className="text-xs font-semibold text-slate-700">Name</TableHead>
                <TableHead className="text-xs font-semibold text-slate-700">Email</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.members.map((m) => (
                <TableRow key={m.id} className="refined-table-row">
                  <TableCell className="text-sm font-medium text-slate-900">{m.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{m.email}</TableCell>
                  <TableCell>
                    {canManageGroups && (
                      <button
                        onClick={() => removeMember(groupId, m.id)}
                        className="p-1.5 rounded hover:bg-red-50 transition-colors"
                        data-testid={`remove-member-${m.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="py-8 text-center border border-slate-200 rounded-lg bg-slate-50">
          <p className="text-sm text-slate-500">No members yet. Add members to get started.</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="refined-group-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="refined-group-header flex items-center justify-between pointer-events-none">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-11 h-11 rounded-lg bg-slate-100 flex-shrink-0" style={{background: 'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize:'200% 100%', animation:'grp-shimmer 1.4s ease infinite'}} />
                <div className="space-y-2 flex-1">
                  <div style={{height:14, width: `${140 + i * 30}px`, borderRadius:6, background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize:'200% 100%', animation:'grp-shimmer 1.4s ease infinite'}} />
                  <div style={{height:11, width: 90, borderRadius:6, background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize:'200% 100%', animation:'grp-shimmer 1.4s ease infinite'}} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <style>{`

        .refined-root {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .refined-header {
          font-family: 'Lexend', sans-serif;
          font-weight: 600;
          letter-spacing: -0.025em;
        }

        .refined-subheader {
          font-family: 'Lexend', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: rgb(71 85 105);
        }

        .refined-group-card {
          background: white;
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          transition: all 0.2s ease;
        }

        .refined-group-card:hover {
          border-color: rgb(203 213 225);
          box-shadow: 0 4px 12px 0 rgb(0 0 0 / 0.06);
        }

        .refined-subgroup-card {
          background: rgb(248 250 252);
          border: 1px solid rgb(226 232 240);
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .refined-subgroup-card:hover {
          border-color: rgb(203 213 225);
          box-shadow: 0 2px 8px 0 rgb(0 0 0 / 0.04);
        }

        .refined-group-header {
          padding: 18px 20px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .refined-group-header:hover {
          background: rgb(248 250 252);
        }

        .refined-detail-section {
          background: linear-gradient(to bottom, rgb(248 250 252), white);
          border-top: 1px solid rgb(226 232 240);
          padding: 20px;
        }

        .refined-subgroup-detail {
          background: white;
          border-top: 1px solid rgb(226 232 240);
          border-radius: 0 0 8px 8px;
          padding: 16px;
        }

        .refined-quiz-stat-card {
          background: white;
          border: 1px solid rgb(226 232 240);
          border-radius: 8px;
          padding: 14px 16px;
          transition: all 0.2s ease;
        }

        .refined-quiz-stat-card:hover {
          border-color: rgb(203 213 225);
          box-shadow: 0 2px 8px 0 rgb(0 0 0 / 0.04);
        }

        .refined-stat-badge {
          font-family: 'Lexend', sans-serif;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: -0.01em;
        }

        .refined-search-input {
          border: 1px solid rgb(226 232 240);
          border-radius: 8px;
          padding-left: 40px;
          height: 42px;
          transition: all 0.2s ease;
        }

        .refined-search-input:focus {
          border-color: rgb(99 102 241);
          box-shadow: 0 0 0 3px rgb(99 102 241 / 0.1);
          outline: none;
        }

        .refined-action-button {
          transition: all 0.2s ease;
        }

        .refined-action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px 0 rgb(0 0 0 / 0.1);
        }

        .refined-dialog-header {
          font-family: 'Lexend', sans-serif;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: rgb(15 23 42);
        }

        .refined-label {
          font-family: 'Lexend', sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: rgb(51 65 85);
        }

        .refined-input {
          border: 1px solid rgb(226 232 240);
          border-radius: 6px;
          height: 40px;
          transition: all 0.2s ease;
        }

        .refined-input:focus {
          border-color: rgb(99 102 241);
          box-shadow: 0 0 0 3px rgb(99 102 241 / 0.1);
          outline: none;
        }

        .refined-textarea {
          border: 1px solid rgb(226 232 240);
          border-radius: 6px;
          transition: all 0.2s ease;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .refined-textarea:focus {
          border-color: rgb(99 102 241);
          box-shadow: 0 0 0 3px rgb(99 102 241 / 0.1);
          outline: none;
        }

        .refined-table {
          border: 1px solid rgb(226 232 240);
          border-radius: 8px;
          overflow: hidden;
        }

        .refined-table-header {
          background: linear-gradient(to bottom, rgb(248 250 252), rgb(241 245 249));
          border-bottom: 1px solid rgb(226 232 240);
        }

        .refined-table-row {
          border-bottom: 1px solid rgb(241 245 249);
          transition: all 0.15s ease;
        }

        .refined-table-row:hover {
          background: rgb(248 250 252);
        }

        .refined-pagination {
          background: linear-gradient(to right, rgb(248 250 252), white, rgb(248 250 252));
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          padding: 16px 20px;
        }

        .refined-empty-state {
          background: linear-gradient(to bottom, white, rgb(248 250 252));
          border: 1px solid rgb(226 232 240);
          border-radius: 12px;
          padding: 64px 24px;
          text-align: center;
        }

        .refined-badge-published {
          background: rgb(240 253 244);
          color: rgb(22 101 52);
          border: 1.5px solid rgb(187 247 208);
          font-family: 'Lexend', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.02em;
        }

        .refined-badge-draft {
          background: rgb(248 250 252);
          color: rgb(71 85 105);
          border: 1.5px solid rgb(226 232 240);
          font-family: 'Lexend', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.02em;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-in {
          animation: fadeIn 0.4s ease;
        }

        .fade-in-delay {
          animation: fadeIn 0.4s ease 0.1s backwards;
        }

        .fade-in-delay-2 {
          animation: fadeIn 0.4s ease 0.2s backwards;
        }

        @keyframes grp-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      <div className="refined-root space-y-6" data-testid="groups-page">
        {/* Header */}
        <div className="flex items-center justify-between fade-in">
          <div>
            <h1 className="refined-header text-3xl text-slate-900 flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg border border-indigo-200">
                <Users className="h-6 w-6 text-indigo-700" />
              </div>
              Groups
            </h1>
            <p className="text-sm text-slate-600 mt-2 ml-14">
              Manage quiz taker groups and assignments
            </p>
          </div>
          {canManageGroups && (
            <Button
              onClick={() => openCreateDialog()}
              data-testid="create-group-btn"
              className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> New Group
            </Button>
          )}
        </div>

        {/* Search + Campus Filter */}
        <div className="flex gap-3 fade-in-delay">
          <div className="relative flex-1">
            <Input
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="refined-search-input"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          </div>
          {(isOrgAdmin || isSuperadmin) && allCampuses.length > 1 && (
            <Select
              value={campusFilter || "all"}
              onValueChange={(v) => { setCampusFilter(v === "all" ? "" : v); setPage(1); }}
            >
              <SelectTrigger className="w-44">
                <MapPin className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                <SelectValue placeholder="All campuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campuses</SelectItem>
                {allCampuses.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Groups List */}
        {groups.length === 0 ? (
          <div className="refined-empty-state fade-in-delay-2">
            {searchTerm ? (
              <>
                <svg width="80" height="72" viewBox="0 0 80 72" fill="none" className="mx-auto mb-5 opacity-80">
                  <circle cx="34" cy="34" r="24" stroke="#C7D2FE" strokeWidth="2" fill="#EEF2FF"/>
                  <circle cx="34" cy="34" r="16" stroke="#A5B4FC" strokeWidth="1.5" fill="none"/>
                  <line x1="52" y1="52" x2="66" y2="66" stroke="#818CF8" strokeWidth="3" strokeLinecap="round"/>
                  <path d="M28 34h12M34 28v12" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
                </svg>
                <p className="text-slate-700 font-semibold mb-1">No groups match "{searchTerm}"</p>
                <p className="text-sm text-slate-500 mb-4">Try a different search term</p>
                <Button variant="outline" size="sm" onClick={() => { setSearchTerm(""); setPage(1); }} className="refined-action-button border-slate-300">
                  Clear search
                </Button>
              </>
            ) : (
              <>
                <svg width="100" height="88" viewBox="0 0 100 88" fill="none" className="mx-auto mb-5 opacity-80">
                  <rect x="10" y="18" width="80" height="56" rx="8" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
                  <circle cx="35" cy="42" r="9" fill="#C7D2FE"/>
                  <circle cx="35" cy="42" r="5" fill="#818CF8"/>
                  <circle cx="55" cy="42" r="9" fill="#C7D2FE"/>
                  <circle cx="55" cy="42" r="5" fill="#818CF8"/>
                  <path d="M22 62c2-6 7-10 13-10s11 4 13 10" stroke="#A5B4FC" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M42 62c2-6 7-10 13-10s11 4 13 10" stroke="#A5B4FC" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="78" cy="22" r="12" fill="#4F46E5"/>
                  <path d="M74 22h8M78 18v8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <p className="text-slate-700 font-semibold mb-1">No groups yet</p>
                <p className="text-sm text-slate-500 mb-5">Create groups to organize quiz takers and assign quizzes</p>
                {canManageGroups && (
                  <Button
                    onClick={() => openCreateDialog()}
                    variant="outline"
                    className="refined-action-button border-slate-300"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Create Your First Group
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3 fade-in-delay-2">
            {groups.map((group) => (
              <div key={group.id} className="refined-group-card" data-testid={`group-card-${group.id}`}>
                {/* Group Header */}
                <div
                  className="refined-group-header flex items-center justify-between"
                  onClick={() => toggleGroupDetail(group.id)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center flex-shrink-0 border border-indigo-200">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{group.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs font-medium text-slate-600">
                          {group.member_count || 0} member{group.member_count !== 1 ? 's' : ''}
                        </span>
                        {group.subgroup_count > 0 && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-xs font-medium text-indigo-600 flex items-center gap-1">
                              <Layers className="w-3 h-3" />
                              {group.subgroup_count} subgroup{group.subgroup_count !== 1 ? 's' : ''}
                            </span>
                          </>
                        )}
                        {allCampuses.length > 1 && group.campus_id && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-xs text-blue-600 font-medium flex items-center gap-0.5">
                              <MapPin className="w-3 h-3" />
                              {allCampuses.find(c => c.id === group.campus_id)?.name || ''}
                            </span>
                          </>
                        )}
                        {group.description && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-xs text-slate-500 truncate">{group.description}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canManageGroups && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => openEditDialog(e, group)}
                        className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                        title="Edit group"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {canManageGroups && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteGroup(group);
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-group-${group.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {expandedGroup === group.id ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Group Detail Section */}
                {expandedGroup === group.id && groupDetail && (
                  <div className="refined-detail-section space-y-6">
                    {/* Assigned Quizzes */}
                    {groupDetail.assigned_quizzes?.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <p className="refined-subheader">
                            Assigned Quizzes
                            {groupDetail.total_member_count > groupDetail.member_count && (
                              <span className="ml-2 normal-case text-slate-400 font-normal" style={{fontSize: '10px'}}>
                                (stats include {groupDetail.total_member_count} total members across subgroups)
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="space-y-2">
                          {groupDetail.assigned_quizzes.map((aq) => (
                            <div
                              key={aq.quiz_id}
                              className="refined-quiz-stat-card"
                              data-testid={`assigned-quiz-${aq.quiz_id}`}
                            >
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-4 h-4 text-indigo-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-slate-900 truncate">{aq.quiz_title}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge className={aq.quiz_status === 'published' ? 'refined-badge-published' : 'refined-badge-draft'}>
                                        {aq.quiz_status}
                                      </Badge>
                                      <span className="text-xs text-slate-400 flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {new Date(aq.assigned_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-5 flex-shrink-0">
                                  <div className="text-right">
                                    <p className="refined-stat-badge text-slate-900">
                                      {aq.unique_test_takers}/{groupDetail.total_member_count || groupDetail.member_count}
                                    </p>
                                    <p className="text-xs text-slate-500 font-medium mt-0.5">Completed</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="refined-stat-badge text-emerald-600">
                                      {aq.completion_rate}%
                                    </p>
                                    <p className="text-xs text-slate-500 font-medium mt-0.5">Completion</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="refined-stat-badge text-indigo-600 flex items-center gap-1">
                                      <TrendingUp className="w-3.5 h-3.5" />
                                      {aq.average_score}%
                                    </p>
                                    <p className="text-xs text-slate-500 font-medium mt-0.5">Avg Score</p>
                                  </div>
                                </div>
                              </div>

                              {/* Subgroup breakdown per quiz */}
                              {aq.subgroup_stats?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                  <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Subgroup Breakdown</p>
                                  <div className="space-y-1">
                                    {aq.subgroup_stats.map((sg) => (
                                      <div key={sg.subgroup_id} className="flex items-center justify-between text-xs text-slate-600 py-1 px-2 rounded bg-slate-50">
                                        <span className="font-medium flex items-center gap-1">
                                          <Layers className="w-3 h-3 text-slate-400" />
                                          {sg.subgroup_name}
                                          <span className="text-slate-400 font-normal">({sg.member_count} members)</span>
                                        </span>
                                        <span className="flex items-center gap-3">
                                          <span>{sg.unique_test_takers}/{sg.member_count} completed</span>
                                          <span className="text-emerald-600 font-semibold">{sg.completion_rate}%</span>
                                          <span className="text-indigo-600 font-semibold">{sg.average_score}% avg</span>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Subgroups Section */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="refined-subheader">
                          Subgroups ({groupDetail.subgroups?.length || 0})
                        </p>
                        {canManageGroups && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCreateDialog({ id: group.id, name: group.name })}
                            className="refined-action-button border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            data-testid={`add-subgroup-${group.id}`}
                          >
                            <Layers className="w-3.5 h-3.5 mr-1" /> Add Subgroup
                          </Button>
                        )}
                      </div>
                      {groupDetail.subgroups?.length > 0 ? (
                        <div className="space-y-2">
                          {groupDetail.subgroups.map((sg) => (
                            <div key={sg.id} className="refined-subgroup-card" data-testid={`subgroup-card-${sg.id}`}>
                              <div
                                className="flex items-center justify-between px-4 py-3 cursor-pointer"
                                onClick={(e) => toggleSubgroupDetail(e, sg.id)}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="w-8 h-8 rounded-md bg-indigo-50 flex items-center justify-center flex-shrink-0 border border-indigo-100">
                                    <Layers className="w-4 h-4 text-indigo-500" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-slate-800 truncate">{sg.name}</p>
                                    <p className="text-xs text-slate-500">
                                      {sg.member_count || 0} member{sg.member_count !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {canManageGroups && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteSubgroup(sg);
                                      }}
                                      className="text-red-400 hover:text-red-600 hover:bg-red-50"
                                      data-testid={`delete-subgroup-${sg.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                  {expandedSubgroup === sg.id ? (
                                    <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                  )}
                                </div>
                              </div>
                              {/* Subgroup expanded detail */}
                              {expandedSubgroup === sg.id && subgroupDetail && subgroupDetail.id === sg.id && (
                                <div className="refined-subgroup-detail space-y-4">
                                  {renderMembersSection(subgroupDetail, sg.id, true)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-6 text-center border border-dashed border-slate-200 rounded-lg bg-slate-50">
                          <p className="text-sm text-slate-400">No subgroups yet.</p>
                        </div>
                      )}
                    </div>

                    {/* Members Section */}
                    {renderMembersSection(groupDetail, group.id)}
                  </div>
                )}
              </div>
            ))}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="refined-pagination flex items-center justify-between">
                <div className="text-sm text-slate-600 font-medium">
                  Page {page} of {pagination.pages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="refined-action-button border-slate-300"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={page === pagination.pages}
                    className="refined-action-button border-slate-300"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Group / Subgroup Dialog */}
      <Dialog
        open={dialog}
        onOpenChange={(open) => {
          setDialog(open);
          if (!open) {
            setFormErrors({});
            setParentGroupForCreate(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="refined-dialog-header">
              {parentGroupForCreate ? `New Subgroup` : "Create New Group"}
            </DialogTitle>
          </DialogHeader>
          {parentGroupForCreate && (
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-100 text-sm text-indigo-700">
              <Layers className="w-4 h-4 flex-shrink-0" />
              <span>Subgroup of: <strong>{parentGroupForCreate.name}</strong></span>
            </div>
          )}
          <div className="space-y-5 mt-2">
            <div className="space-y-2">
              <Label className="refined-label">
                {parentGroupForCreate ? "Subgroup Name" : "Group Name"}
              </Label>
              <Input
                value={form.name}
                onChange={(e) => {
                  setForm((p) => ({ ...p, name: e.target.value }));
                  setFormErrors((p) => ({ ...p, name: undefined }));
                }}
                data-testid="group-name-input"
                className={`refined-input ${formErrors.name ? 'border-red-400' : ''}`}
                placeholder={parentGroupForCreate ? "e.g. Batch A" : "e.g. Engineering Team"}
              />
              {formErrors.name && <p className="text-xs text-red-500 font-medium">{formErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label className="refined-label">Description (optional)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                data-testid="group-desc-input"
                className="refined-input"
                placeholder="Brief description"
              />
            </div>
            {(isOrgAdmin || isSuperadmin) && allCampuses.length > 0 && !parentGroupForCreate && (
              <div className="space-y-2">
                <Label className="refined-label">Campus (optional)</Label>
                <Select
                  value={form.campus_id || "__none__"}
                  onValueChange={(v) => setForm((p) => ({ ...p, campus_id: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger className="refined-input">
                    <SelectValue placeholder="All campuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No specific campus</SelectItem>
                    {allCampuses.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={createGroup}
              data-testid="save-group-btn"
              className="w-full refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white h-10"
            >
              {parentGroupForCreate ? "Create Subgroup" : "Create Group"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="refined-dialog-header">Edit Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="refined-label">Group Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                className="refined-input"
                placeholder="Group name"
              />
              {editFormErrors.name && <p className="text-xs text-red-500">{editFormErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label className="refined-label">Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                className="refined-input"
                placeholder="Brief description"
              />
            </div>
            {(isOrgAdmin || isSuperadmin) && allCampuses.length > 0 && (
              <div className="space-y-2">
                <Label className="refined-label">Campus (optional)</Label>
                <Select
                  value={editForm.campus_id || "__none__"}
                  onValueChange={(v) => setEditForm((p) => ({ ...p, campus_id: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger className="refined-input">
                    <SelectValue placeholder="All campuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No specific campus</SelectItem>
                    {allCampuses.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={saveEditGroup}
              disabled={updateGroupMutation.isPending}
              className="w-full refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white h-10"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={memberDialog} onOpenChange={setMemberDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="refined-dialog-header">Add Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="refined-label">Full Name</Label>
              <Input
                value={memberForm.name}
                onChange={(e) => setMemberForm((p) => ({ ...p, name: e.target.value }))}
                data-testid="member-name-input"
                className="refined-input"
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label className="refined-label">Email Address</Label>
              <Input
                type="email"
                value={memberForm.email}
                onChange={(e) => setMemberForm((p) => ({ ...p, email: e.target.value }))}
                data-testid="member-email-input"
                className="refined-input"
                placeholder="john@company.com"
              />
            </div>
            <Button
              onClick={addMember}
              data-testid="save-member-btn"
              className="w-full refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white h-10"
            >
              Add Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Members Dialog */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="refined-dialog-header">Bulk Add Members</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 font-medium">Enter members in CSV format</p>
              <Button
                variant="link"
                size="sm"
                onClick={downloadSampleCsv}
                className="text-indigo-600 p-0 h-auto hover:text-indigo-700"
                data-testid="download-sample-csv"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Download Template
              </Button>
            </div>
            <div className="space-y-2">
              <Textarea
                value={bulkCsv}
                onChange={(e) => setBulkCsv(e.target.value)}
                placeholder="email,name&#10;john@example.com,John Doe&#10;jane@example.com,Jane Smith"
                className="refined-textarea min-h-[180px] text-sm"
                data-testid="bulk-csv-input"
              />
              <p className="text-xs text-slate-500">One member per line. First row can be a header.</p>
            </div>
            <Button
              onClick={bulkAddMembers}
              disabled={bulkAddMembersMutation.isPending}
              data-testid="save-bulk-members-btn"
              className="w-full refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white h-10"
            >
              {bulkAddMembersMutation.isPending ? "Adding Members..." : "Add Members"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
